import React from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle2, ClipboardList, XCircle } from 'lucide-react';
import Button from '../../../../components/ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from '../../../../components/ui/Dialog';
import { SifPreflightView } from '../../../../models/payroll';

interface PreflightChecklistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preflight: SifPreflightView | null;
  /** The two countries word their issue codes differently. */
  issueLabel: (code: string) => string;
}

/** The existing pre-flight checklist. A summary a person asks for, so it stays a dialog. */
export const PreflightChecklistDialog: React.FC<PreflightChecklistDialogProps> = ({ open, onOpenChange, preflight, issueLabel }) => {
  const { t } = useTranslation();
  const warningIssues = preflight?.employeeIssues?.filter(e => e.isWarning) ?? [];
  const blockingIssues = preflight?.employeeIssues?.filter(e => !e.isWarning) ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-primary" />
            {t('payroll.wps.preflightChecklist', 'Pre-flight Checklist')}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          {preflight && (
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-semibold mb-2">{t('payroll.wps.companyChecksTitle', 'Company Checks')}</h4>
                {preflight.companyChecks.length === 0 ? (
                  <p className="text-sm text-muted-foreground">{t('payroll.wps.noCompanyChecks', 'No company-level checks.')}</p>
                ) : (
                  <ul className="space-y-2">
                    {preflight.companyChecks.map((check, i) => (
                      <li key={check.code || i} className="flex items-start gap-2">
                        {check.passed ? (
                          <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
                        ) : check.isWarning ? (
                          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                        ) : (
                          <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                        )}
                        <div>
                          <p className={`text-sm ${check.passed ? 'text-foreground' : check.isWarning ? 'text-amber-700' : 'text-red-700'}`}>
                            {t(check.label, check.label)}
                          </p>
                          {check.detail && (
                            <p className="text-xs text-muted-foreground">{check.detail}</p>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {blockingIssues.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-red-700">
                    {t('payroll.wps.blockingIssuesTitle', 'Blocking Issues')} ({blockingIssues.length})
                  </h4>
                  <ul className="space-y-1.5">
                    {blockingIssues.map((issue, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-red-700">{issue.employeeName}</p>
                          <p className="text-xs text-muted-foreground">{issueLabel(issue.issue)}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {warningIssues.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold mb-2 text-amber-700">
                    {t('payroll.wps.warningIssuesTitle', 'Coverage Warnings')} ({warningIssues.length})
                  </h4>
                  <ul className="space-y-1.5">
                    {warningIssues.map((issue, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-sm text-amber-700">{issue.employeeName}</p>
                          <p className="text-xs text-muted-foreground">{issueLabel(issue.issue)}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {blockingIssues.length === 0 && warningIssues.length === 0 && preflight.companyPassed && (
                <div className="flex items-center gap-2 text-emerald-700 text-sm">
                  <CheckCircle2 className="w-4 h-4" />
                  {t('payroll.wps.allClear', 'All checks passed. Ready to generate.')}
                </div>
              )}
            </div>
          )}
        </DialogBody>
        <DialogFooter className="justify-center">
          <Button onClick={() => onOpenChange(false)}>
            {t('common.ok', 'OK')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
